import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HabitHistoryPanel, amount, toTargetLabel } from './HabitHistory';
import { habitFigure, ringFigure } from '../habitFigure';
import { QueryState } from './QueryState';
import { runFigure, staleReadingSince, verdictWord } from '../habitVocab';
import { byUndone, readOrder, useLogHold, writeOrder, type HabitOrder } from '../habitOrder';
import { LogHabit } from './LogHabit';
import {
  getHabitsStatus,
  getGoalTree,
  createHabit,
  deleteHabit,
  updateHabit,
  logHabit,
  undoHabitLog,
  getHabitHeatmap,
  getHabitHistory,
  isMarginalBreach,
  ApiError,
  type HabitProgress,
  type HabitPolarity,
  type HabitPeriod,
  type HabitAggregation,
} from '../api';
import { HABIT_PERIOD_NOUN, habitPausedSince } from '../taskVocab';
import { Select } from './Select';
import { goalOptions, renderGoalOption } from './pickerOptions';
import { PeriodStrip, StripLegend } from './PeriodStrip';
import { shiftDay, todayInZone } from '../timezones';
import { readableDay, readableRange } from '../dateLabels';

/**
 * Which goal this habit serves.
 *
 * `habit.goal_id` has been in the schema and the API since Phase 3 and the web UI never offered
 * it — so a running habit and a "run a Spartan trifecta" goal sat in the same app knowing
 * nothing about each other, and the goal read 0% forever while the running happened.
 */
function GoalSelect({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  const goals = useQuery({ queryKey: ['goals', 'tree'], queryFn: getGoalTree });
  const options = goalOptions(goals.data ?? []);
  if (options.length === 0) return null;

  return (
    <label>
      Serves goal
      <Select
        options={options}
        value={value || null}
        onChange={(next) => onChange(next ?? '')}
        placeholder="None"
        ariaLabel="Serves goal"
        renderOption={renderGoalOption}
      />
    </label>
  );
}

/** 0 = Monday … 6 = Sunday, matching `weekdayOf` — not `Date.getDay`. */
const WEEKDAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * A ring rather than a bar: the period is a cycle, and a ring reads as "how much of
 * this period's target is done" without implying a finish line further right.
 *
 * For a measurement the fraction is not `actual / target`. That expression is capped at 1, and a
 * body weight of 78.84 kg against a target of 78 gives exactly 1 — the same full ring the habit
 * drew at 300 kg, and the same one it will draw at every reading for the rest of its life. The
 * band the server supplies is a position between the worst reading on record and the target, so
 * the ring moves when the reading moves, which is the only thing a ring is for.
 */
function ProgressRing({
  progress,
  polarity,
}: {
  progress: HabitProgress;
  polarity: HabitPolarity;
}) {
  const radius = 26;
  const circumference = 2 * Math.PI * radius;
  const fraction = progress.band ? progress.band.position : Math.min(1, progress.ratio);
  const filled = fraction * circumference;
  const label = ringFigure({ ...progress, polarity });

  /*
    A limit is a threshold, not a cliff, and the ring is where that has to show.

    Every breach was `bad`, so a cut running 0.84 kg over its ceiling was painted the same red as
    one running 80 kg over — every morning, for the rest of the period. A breach inside
    `LIMIT_MARGIN` is the same amber as being *at* the limit, which is what it is: over the line
    and worth saying so, not a failed period.
  */
  const tone =
    progress.state === 'breached'
      ? isMarginalBreach(progress)
        ? 'warn'
        : 'bad'
      : progress.state === 'behind' || progress.state === 'at_limit'
        ? 'warn'
        : /*
            An unmeasured ceiling is not `ok`: `ok` is the colour of a limit held, and there is no
            evidence this one was. Neutral rather than `warn` — nobody is in trouble for not
            having weighed themselves yet, and the empty ring says the rest.

            A paused habit takes the same neutral, by the same argument and not by the same fact:
            `ok` would paint a target nobody is keeping as one that is being kept. The two states
            share a colour and nothing else — the word beside the ring is what tells them apart.
          */
          progress.state === 'unmeasured' || progress.state === 'paused'
          ? 'idle'
          : 'ok';

  return (
    <svg
      className={`ring tone-${tone}`}
      viewBox="0 0 64 64"
      width="64"
      height="64"
      aria-hidden="true"
    >
      <circle className="ring-track" cx="32" cy="32" r={radius} fill="none" strokeWidth="6" />
      <circle
        className="ring-fill"
        cx="32"
        cy="32"
        r={radius}
        fill="none"
        strokeWidth="6"
        strokeLinecap="round"
        strokeDasharray={`${filled} ${circumference}`}
        transform="rotate(-90 32 32)"
      />
      {/*
        The figure of a habit you are doing, and nothing on a limit — see `ringFigure`.

        Smaller as it grows longer, so it stays inside the stroke. It is `amount()`'s figure now,
        the line's to two decimals, and *12.46* at the label's own size measured 53px (this box's
        renderer, not CI's) across a stroke whose inner edge is 46px apart; rounding it to fit made
        the ring say 12.5 beside the line's 12.46. Four characters fit at full size, so the size
        falls as 4/length beyond that, which holds the label's width at what four characters take.
      */}
      {label !== null && (
        <text
          className="ring-label"
          x="32"
          y="36"
          textAnchor="middle"
          style={label.length > 4 ? { fontSize: `${4 / label.length}rem` } : undefined}
        >
          {label}
        </text>
      )}
    </svg>
  );
}

/**
 * What one heatmap square is allowed to claim about one day.
 *
 * Exported and pure so it can be tested exhaustively — the bug it replaces survived a release
 * because it could only be seen by looking at a rendered year, and the test that was meant to
 * cover it used the one habit shape the bug did not have.
 *
 * The facts that decide it:
 *
 * - **Was there a reading at all?** `null` means no entry. This used to be filled in as `0`
 *   before it reached here, which is what destroyed the distinction.
 * - **Is the habit abstinence or a ceiling?** Both are "avoid" and they are read oppositely.
 *   On abstinence (`at most 0`) an empty day *is* the achievement. On a ceiling (`at most 115`)
 *   an empty day is a morning you did not measure, and says nothing at all.
 * - **Is the ceiling read or tallied?** `reading` — the service's `isReading`. A blood pressure
 *   nobody took is an absence whoever was around. A tallied ceiling — "at most 2 units a week",
 *   logged as the drinks drunk — is kept by not logging, so an empty day the account was here for
 *   is a day at nothing: the strip's rule for the same habit, which calls a week with use in it
 *   and no entry `0 units of 2 allowed — met`. At day grain `periodWasUsed` and `silenceIsClean`
 *   are one predicate, so this is the strip's answer for a one-day period, not a new one.
 * - **Had the habit started?** `beforeStart` — days before `trackedFrom` claim nothing, for any
 *   polarity. Not doing something you had not decided to stop doing is not an achievement, and a
 *   day before the record began is not a measured zero either: the grid printed *0 units* on
 *   eight months of the maximizer's Alcohol before its first entry, in the tint of a measured day.
 * - **Was the account here that day?** `used` — any habit entry or any task finished, from the
 *   service's `daysAccountUsed`. On abstinence an empty day is the achievement only when somebody
 *   was around to not drink; a day nobody opened the app is *not measured*, the word the strip and
 *   the "clean days" head already use for it. Without this the health nut's Alcohol grid read
 *   *clean* on every day of September, over an account with entries on three days in two months.
 *
 * `withinLimit` is the **colour**, not the word. A day's entry is held against the whole
 * allowance (see `overshoot`), so 2 units on a 2-a-week ceiling draws green — Phase 52's call, and
 * right for the colour. What the square *says* is `heatmapTitle`'s, and it says *clean* only on a
 * day at nothing, and *within* or *over* only on a daily ceiling, where the day is the period.
 */
export function heatmapVerdict(input: {
  value: number | null;
  day: string;
  trackedFrom: string;
  avoid: boolean;
  target: number;
  used: boolean;
  reading: boolean;
}): HeatmapVerdict {
  const { value, day, trackedFrom, avoid, target, used, reading } = input;
  const measured = value !== null;
  const nothing = { slip: false, withinLimit: false, absent: false, measured };

  if (day < trackedFrom) return { ...nothing, beforeStart: true };
  if (!avoid) return { ...nothing, beforeStart: false };

  const abstinence = target === 0;
  /*
    Silence is the habit kept on abstinence and on a tallied ceiling — on a day somebody was here.
    A ceiling you read is never kept by silence: a morning not weighed says nothing.
  */
  const silenceKeeps = !reading && used;
  const slip = abstinence ? measured : measured && value > target;
  const withinLimit = measured ? !slip : silenceKeeps;
  const absent = !measured && !silenceKeeps;
  return { slip, withinLimit, absent, measured, beforeStart: false };
}

type HeatmapVerdict = {
  slip: boolean;
  withinLimit: boolean;
  absent: boolean;
  measured: boolean;
  beforeStart: boolean;
};

/**
 * The square's hover text — the one place it is assembled, so a change to how the day is written
 * reaches every outcome at once.
 *
 * *clean* is a day at nothing on a limit, and nothing else. It used to be any day inside the
 * allowance, so the maximizer's Alcohol (at most 2 units a week) read *clean* on 27 Jun and 10 Jul
 * with 2 units logged on each, and on 16 Aug with 1 — hiding the very figure the grid is there to
 * show, beside an "All time 15 units" the squares no longer added up to. A ceiling's measured day
 * now carries its amount and the allowance, in the strip's own figure for a target.
 *
 * Only a **daily** ceiling says which side of it the day fell: there the day *is* the period, so
 * `111 mmHg (within 115/day)` is the strip's verdict in other words. A weekly or monthly ceiling
 * says `2 units · limit 2/week` and no side. A day cannot be within a week's allowance by itself:
 * the maximizer's 10 Jul (2 units) and 11 Jul (3) share a week totalling 5, and "within 2/week" on
 * 10 Jul sat beside a strip calling that week missed. Judging the side against the period's total
 * was the other way to make it true, and was not taken: the total is the strip's — aggregation,
 * week start, a period running past the grid's window — and restating that rule here is how two
 * surfaces come to disagree about one week. The strip says the period's verdict; the square says
 * the day's figure. The colour still holds the day against the whole allowance (Phase 52), and
 * the words no longer claim more than the colour does.
 */
export function heatmapTitle(
  cell: { day: string; value: number | null },
  verdict: HeatmapVerdict,
  habit: { avoid: boolean; target: number; unit: string | null; period: string },
): string {
  const { value } = cell;
  /*
    The day as a person reads it — *27 Jun* — not the ISO key, which stays on the square as
    `data-day` for anything that needs the machine form. See `dateLabels.ts`.
  */
  const day = readableDay(cell.day);
  if (verdict.beforeStart) return `${day}: before tracking`;
  if (verdict.absent) return `${day}: not measured`;
  if (habit.avoid && verdict.withinLimit && (value ?? 0) === 0) return `${day}: clean`;
  const figure = amount(value ?? 0, habit.unit);
  if (habit.avoid && habit.target > 0) {
    const noun = HABIT_PERIOD_NOUN[habit.period] ?? HABIT_PERIOD_NOUN.week!;
    const limit = `${amount(habit.target, null)}/${noun.one}`;
    if (habit.period !== 'day') return `${day}: ${figure} · limit ${limit}`;
    const side = verdict.slip ? 'over' : 'within';
    return `${day}: ${figure} (${side} ${limit})`;
  }
  return `${day}: ${figure}`;
}

/** GitHub-style year heatmap, weeks as columns. */
function Heatmap({
  habitId,
  max,
  daysPerPeriod,
  avoid,
  reading,
  period,
  timezone,
  unit,
}: {
  habitId: string;
  max: number;
  /** The service's `isReading` — whether an empty day on a ceiling can be a day at nothing. */
  reading: boolean;
  /** The habit's period, which a ceiling day's title names its allowance in: `2/week`. */
  period: string;
  /**
   * For the day's hover text, which went out as `${cell.value}` — a day logged as 16.8 + 1.1 read
   * `17.900000000000002` (`01a08094-6da4`). `amount()` rounds it and spells the unit, as the strip
   * and the chart above it do.
   */
  unit: string | null;
  /** Days in the habit's period, so a weekly target is not used as a daily ceiling. */
  daysPerPeriod: number;
  /**
   * A habit you are trying *not* to do, where the good day is the empty one.
   *
   * The grid shaded by value and never read polarity, so on "No doomscrolling" the darkest three
   * cells of the whole year were the three days it happened — 0.72 against 0.12 for every clean
   * day, which is failure rendered as the brightest thing on the screen. The same card contradicted
   * itself twice over: its 14-day strip drew clean days green, and its 90-day chart drew slips as
   * amber spikes. Only the year grid ran backwards, which is the inversion the strip itself had
   * before Phase 20.
   */
  avoid: boolean;
  /**
   * The account's zone, so the year ends on the day the account is on.
   *
   * `to` came off `new Date().toISOString()` — the UTC day — and the drawing loop stops at it. An
   * account ahead of UTC therefore had no cell for today for as long as UTC lagged it (Kiritimati
   * until 14:00, CEST between midnight and 02:00), and one behind UTC got a cell for a day it had
   * not reached yet. Same zone the rest of `HabitCard` reads, threaded from the same prop.
   */
  timezone: string;
}) {
  const to = todayInZone(timezone);
  const from = shiftDay(to, -364);

  const data = useQuery({
    queryKey: ['heatmap', habitId, from, to],
    queryFn: () => getHabitHeatmap(habitId, from, to),
  });
  /*
    Before `trackedSince`, the habit did not exist, and its absence is not an achievement.

    Without a floor at all, a four-day-old "No doomscrolling" painted 363 clean days going back to
    last August, which is the same lie as the grid it replaces, told the other way up. A day is only
    clean once you were keeping it so.

    The floor is the history payload's `trackedSince` — `activeFrom ?? createdAt`, pulled back to
    the first entry on the *whole* record — under the same query key the history panel above this
    grid already fetches, so it costs no request. It was derived here from the earliest entry
    inside the 365-day window, which made a habit older than the window start wherever the window
    did: the fourth window-bound start `01a15e92-2c2a` named, and the one it left for this grid.
  */
  const history = useQuery({
    queryKey: ['habit-history', habitId],
    queryFn: () => getHabitHistory(habitId),
  });

  /*
    Opened on the recent end.

    At `scrollLeft: 0` a card reading "58 sessions all time" showed 168 of 370 cells spanning
    2025-08-18 → 2026-02-01 with **none of the 58 filled cells in view** — so the default state of
    the year view was proof you had never done the habit.

    In an effect rather than a ref callback: the callback fires before the columns have been laid
    out, so `scrollWidth` is still the client width and the assignment is a no-op.
  */
  const scroller = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [data.data]);

  if (data.isPending || data.isError) return <QueryState query={data} />;
  if (history.isPending || history.isError) return <QueryState query={history} />;

  const values = new Map<string, number>();
  for (const d of data.data ?? []) if (d.value !== null) values.set(d.day, d.value);
  /* Days the account was here at all — see `heatmapVerdict` for what that changes. */
  const usedOn = new Set((data.data ?? []).filter((d) => d.used).map((d) => d.day));

  /*
    A year of empty squares is not a year of data.

    On a habit a few days old this drew 365 cells at the palest step with a handful barely
    distinguishable from them — a grid that looks broken rather than new. Below a week of entries
    there is no shape to show, so it says so instead.
  */
  const logged = [...values.values()].filter((v) => v > 0).length;
  /*
    Except on a habit you are trying not to do, where nothing logged is the best year available.

    The gate counts entries as evidence, which is the right test for a habit you are trying to do
    and exactly backwards for the other kind: a spotless year of "No doomscrolling" has zero
    entries and was told "No history yet — this fills in as you log." What makes an avoid habit's
    grid worth drawing is having been active long enough to show a stretch, so that is what is
    asked instead.
  */
  /*
    The window starts where the record does, which is not always where the habit does.

    `activeFrom` is null on most habits — nobody backdates one — so the floor falls back to
    `createdAt`; and entries are routinely logged *before* that, because you set a habit up after
    the thing you are tracking has already been happening. The maximizer's "No doomscrolling" was
    created on the 19th carrying slips from the 13th, and a floor at `createdAt` alone would say
    those three days were before it existed and then decline to draw the year at all.
    `trackedSince` already includes that pull-back, over the whole record.
  */
  const trackedFrom = history.data.trackedSince;
  const trackedDays = Math.round((Date.parse(to) - Date.parse(trackedFrom)) / 86_400_000);
  /* An avoid habit's evidence is time passed, not entries made — a spotless year has none. */
  if ((avoid ? Math.max(trackedDays, logged) : logged) < 7) {
    return (
      <p className="subtext" data-testid="heatmap-empty">
        {avoid
          ? 'Too new to show a year — this fills in as the days pass.'
          : logged === 0
            ? 'No history yet — this fills in as you log.'
            : `Only ${logged} day${logged === 1 ? '' : 's'} logged so far — the year fills in from here.`}
      </p>
    );
  }
  /*
    Scaled against a *daily* figure, not the habit's period target.

    `max` is the target — weekly or monthly — used as the ceiling for a single day's cell, so
    every real day sat in a narrow band near the bottom. A persona's 49 lifting days spanned
    opacity 0.466 to 0.561, and a year of body-weight readings spanned 0.96 to 1.00: the heatmap
    was binary logged/not-logged for anything whose period is not a day. `PeriodStrip` on the
    Today screen already had the right formula.
  */
  const observed = [...values.values()].filter((v) => v > 0);
  const perDay = max / Math.max(1, daysPerPeriod);
  const peak = Math.max(perDay, ...observed, 1);
  // A floor as well as a ceiling: when readings never approach zero — a body weight, a resting
  // heart rate — scaling from zero puts every cell at the top of the ramp and flattens them.
  const floor = observed.length > 0 ? Math.min(...observed) : 0;
  const spread = Math.max(peak - floor, peak * 0.05, 1e-9);

  /*
    An occurrence habit has no amounts to ramp, so the ramp is density instead.

    Every entry on "Strength training" is the number 1, so `floor` equals the observed peak and the
    whole scale collapses: a sweep of all 370 cells returned exactly two opacities, 0.12 for the
    341 empty days and 0.35 for the 29 logged ones. That describes most of what somebody tracking
    behaviour has — a thing is done or it is not — and it renders a year of perfect adherence as a
    near-empty pale grid.

    For those the interesting variable is not how much but how often, so a cell is shaded by how
    many of the seven days around it were logged. A solid month becomes solid; a scattered one
    stays scattered; and the two are finally different pictures.

    Habits whose amounts genuinely vary keep the amount ramp — that is real information and this
    would throw it away.
  */
  const varied = observed.length > 1 && Math.max(...observed) - Math.min(...observed) > 1e-9;
  const densityAt = (day: string) => {
    const at = new Date(`${day}T00:00:00Z`).getTime();
    let logged = 0;
    for (let offset = -3; offset <= 3; offset += 1) {
      const near = new Date(at + offset * 86_400_000).toISOString().slice(0, 10);
      if ((values.get(near) ?? 0) > 0) logged += 1;
    }
    return logged / 7;
  };

  // Start on the Monday on or before the range start, so columns line up as weeks.
  const start = new Date(`${from}T00:00:00Z`);
  start.setUTCDate(start.getUTCDate() - ((start.getUTCDay() + 6) % 7));

  /*
    `null` means no entry, which is not the same number as zero.

    This filled every gap with `0` and then read `value > 0` as a slip, so on a numeric ceiling —
    blood pressure, body fat, resting heart rate — the grid painted every morning you *measured*
    in amber and every morning you *skipped* in green, and called the skipped ones clean. The
    health nut's sweep of 359 cells found no exception: 111 mmHg under a 115 limit was a slip.

    Abstinence is the case that made `?? 0` look right, and it is the only one it is right for:
    there, no entry genuinely is the achievement.
  */
  const weeks: { day: string; value: number | null }[][] = [];
  for (let w = 0; w < 53; w += 1) {
    const week: { day: string; value: number | null }[] = [];
    for (let d = 0; d < 7; d += 1) {
      const cell = new Date(start.getTime() + (w * 7 + d) * 86_400_000);
      const day = cell.toISOString().slice(0, 10);
      if (day > to) break;
      week.push({ day, value: values.get(day) ?? null });
    }
    if (week.length) weeks.push(week);
  }

  /*
    Month ticks, so 371 squares have a scale.

    A column gets a label when its first day is in a different month from the previous column's —
    which is the first column of each month, and nothing else.
  */
  const monthOf = (iso: string) => iso.slice(0, 7);
  const labels = weeks.map((week, i) => {
    const first = week[0]!.day;
    if (i > 0 && monthOf(first) === monthOf(weeks[i - 1]![0]!.day)) return '';
    return readableDay(first, 'month');
  });

  return (
    <div className="heatmap" role="img" aria-label="Activity over the past year" ref={scroller}>
      {weeks.map((week, i) => (
        <div className="heatmap-week" key={week[0]!.day}>
          <span className="heatmap-month" aria-hidden="true">
            {labels[i]}
          </span>
          {week.map((cell) => {
            /*
              An avoid habit is read the other way up.

              The clean day is the achievement, so it takes the strong colour and takes it flat —
              a clean Tuesday is not more or less clean than a clean Friday, and there is nothing
              to ramp. The slip is the exception, drawn in the warning colour the 90-day chart on
              this same card already uses for it, and ramped by how much: on a numeric limit
              ("at most 2"), a day inside the day's share of the allowance is a smaller mark than
              one well past it, and on abstinence there is no share, so any entry is the full mark.

              Days before the habit existed stay at the base tint. Not doing something you had not
              decided to stop doing is not a clean day, and a grid that paints the eight months
              before you started as a triumph is the same lie as the one this replaces.
            */
            const verdict = heatmapVerdict({
              value: cell.value,
              day: cell.day,
              trackedFrom,
              avoid,
              target: max,
              used: usedOn.has(cell.day),
              reading,
            });
            const { slip, withinLimit, absent, measured } = verdict;
            /* Against the whole allowance, not the day's share of it: one drink of the two a week
               allows is a smaller mark than both, and abstinence allows nothing, so any entry on
               it is the full mark. */
            const overshoot = Math.min(1, (cell.value ?? 0) / Math.max(max, 1));
            return (
              <span
                key={cell.day}
                className={`heatmap-cell${slip ? ' is-slip' : ''}${absent ? ' is-absent' : ''}`}
                /* The day in an attribute as well as the title, so a spec can ask which day a
                   cell is without parsing the sentence the title writes around it. */
                data-day={cell.day}
                title={heatmapTitle(cell, verdict, { avoid, target: max, unit, period })}
                style={{
                  opacity: slip
                    ? 0.55 + 0.45 * overshoot
                    : withinLimit
                      ? 0.7
                      : !measured
                        ? 0.12
                        : varied
                          ? 0.35 + 0.65 * Math.min(1, ((cell.value ?? 0) - floor) / spread)
                          : /* See `densityAt`: with one repeated amount there is nothing to ramp. */
                            0.35 + 0.65 * densityAt(cell.day),
                }}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}

/**
 * Changing a habit after it exists.
 *
 * The only way to change a target before this was to delete the habit and start again,
 * which destroys every entry it ever had — so "make it 3x a week instead of 4" cost you
 * your history, and the honest answer was to leave the wrong target in place and let the
 * drift warnings be wrong. A frequency target is a guess about your own life; being unable
 * to revise it is the one thing that must not be expensive.
 */
function EditHabitForm({ habit, onDone }: { habit: HabitProgress['habit']; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(habit.name);
  const [polarity, setPolarity] = useState<HabitPolarity>(habit.polarity);
  const [targetCount, setTargetCount] = useState(String(habit.targetCount));
  const [period, setPeriod] = useState<HabitPeriod>(habit.period);
  const [unit, setUnit] = useState(habit.unit ?? '');
  const [aggregation, setAggregation] = useState<HabitAggregation>(habit.aggregation ?? 'sum');
  const [description, setDescription] = useState(habit.description);
  const [goalId, setGoalId] = useState(habit.goalId ?? '');

  const save = useMutation({
    mutationFn: () =>
      updateHabit(habit.id, {
        name: name.trim(),
        polarity,
        targetCount: Number(targetCount),
        period,
        unit: unit.trim() === '' ? null : unit.trim(),
        description,
        aggregation,
        goalId: goalId === '' ? null : goalId,
      }),
    onSuccess: () => {
      onDone();
      void queryClient.invalidateQueries();
    },
  });

  const isAbstinence = Number(targetCount) === 0;
  // Changing the period reinterprets history rather than discarding it: the same entries
  // are re-totalled against the new bounds, which is worth saying before it happens.
  const periodChanged = period !== habit.period;

  return (
    <form
      className="habit-edit"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim()) save.mutate();
      }}
    >
      <div className="fields">
        <label>
          Name
          <input value={name} aria-label="Habit name" onChange={(e) => setName(e.target.value)} />
        </label>
        <label>
          Direction
          <select
            value={polarity}
            aria-label="Direction"
            onChange={(e) => setPolarity(e.target.value as HabitPolarity)}
          >
            <option value="positive">At least this often</option>
            <option value="negative">At most this often</option>
          </select>
        </label>
        <label>
          Target
          <input
            type="number"
            min={0}
            step="any"
            value={targetCount}
            aria-label="Target"
            onChange={(e) => setTargetCount(e.target.value)}
          />
        </label>
        <label>
          Per
          <select
            value={period}
            aria-label="Period"
            onChange={(e) => setPeriod(e.target.value as HabitPeriod)}
          >
            <option value="day">day</option>
            <option value="week">week</option>
            <option value="month">month</option>
          </select>
        </label>
        <label>
          Unit
          <input
            value={unit}
            aria-label="Unit"
            placeholder="sessions"
            onChange={(e) => setUnit(e.target.value)}
          />
        </label>
        <label>
          Measures
          {/*
            How a period's figure is read from the entries. Summing is right for a quantity you
            accumulate towards a target and meaningless for a measurement you take — a week of
            body-weight entries summed to 545 kg on a persona's account.
          */}
          <select
            value={aggregation}
            aria-label="How this is measured"
            onChange={(e) => setAggregation(e.target.value as HabitAggregation)}
          >
            <option value="sum">a total to reach (sessions, km, pages)</option>
            <option value="latest">the latest reading (weight, resting heart rate)</option>
            <option value="mean">the period average (body fat, HRV)</option>
          </select>
        </label>
        <GoalSelect value={goalId} onChange={setGoalId} />
      </div>

      <label className="subtext" htmlFor={`habit-why-${habit.id}`}>
        Note to yourself
      </label>
      <textarea
        id={`habit-why-${habit.id}`}
        rows={2}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />

      {periodChanged && (
        <p className="banner" data-testid="period-change-notice">
          Every entry is kept — they will be re-totalled against the {period}.
        </p>
      )}
      {isAbstinence && (
        <p className="banner ok-banner">
          Target zero: this becomes an abstinence habit, counted in clean days.
        </p>
      )}
      {save.error instanceof ApiError && <p className="banner">{save.error.message}</p>}

      <div className="detail-foot">
        <button type="submit" className="primary" disabled={!name.trim() || save.isPending}>
          Save
        </button>
        <button type="button" className="secondary" onClick={onDone}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function HabitCard({
  progress,
  timezone,
  onLogged,
  held = false,
  reveal = false,
}: {
  progress: HabitProgress;
  /**
   * The account's zone, so this card's "today" is the one the server bucketed by.
   *
   * `progress.byDay` and `progress.strip` are cut server-side on the account's local day. This
   * read its own from `todayIso()` — the browser's — which for an account in another zone is a
   * third clock: the undo control appeared on a day the account had already left, and the strip
   * highlighted the wrong cell. `Today.tsx` has taken the zone as a prop since Phase 20 for the
   * same reason; this is that convention, not a second one.
   */
  timezone: string;
  /** So a filtered list can keep this row where it is — see `held` in `Habits`. */
  onLogged?: (habitId: string) => void;
  /** Kept on screen despite no longer matching the filter, because you just acted on it. */
  held?: boolean;
  /**
   * Arrived here from a notification or a search result naming this habit.
   *
   * Both parsed the id and then dropped it: a habit notification landed on the Habits screen at
   * scrollY 0 with the card collapsed, which on a 31-habit account is an eight-thousand-pixel
   * scroll to reach the thing the app has just told you about. A task notification has always
   * been given `onOpenTask(id)`.
   */
  reveal?: boolean;
}) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(reveal);
  const card = useRef<HTMLLIElement>(null);

  /*
    Scrolled to as well as opened: expanding a card 8,000px down the page is not arriving at it.

    To the card's top, not its centre. `block: 'center'` centres the whole expanded card, and once
    the card is taller than the window that puts its own name above the top edge — at 375x812 the
    card landed at y = -108 the moment the backfill form grew its Yesterday buttons. The name is
    what tells you which habit you were sent to; `scroll-margin-top` on `.habit-card` keeps it
    clear of the sticky header.
  */
  useEffect(() => {
    if (!reveal) return;
    setExpanded(true);
    card.current?.scrollIntoView({ block: 'start' });
  }, [reveal, progress.habit.id]);
  const [editing, setEditing] = useState(false);
  const habit = progress.habit;

  const invalidate = () => queryClient.invalidateQueries();

  /*
    The value is passed now.

    This called `logHabit(habit.id)` with no body, so the server's default of 1 applied to every
    habit whatever its unit — pressing "+1" on a habit measured in kilometres recorded one
    kilometre, and there was no amount field on this screen to correct it with. The day view had
    asked for the amount since Phase 4 and gained the one-press shortcut in Phase 25; the screen
    actually *about* habits was never brought along.
  */
  const log = useMutation({
    mutationFn: (value: number) => logHabit(habit.id, { value }),
    onSuccess: () => {
      onLogged?.(habit.id);
      invalidate();
    },
  });
  /*
    Undo, where the logging happens, for as long as there is something to take back.

    `undoHabitLog` was wired into Today alone, while the Habits screen — which has the search box
    and the Behind filter, and is therefore where this persona actually logs — had none. Recovery
    was: expand the card, scroll to "Entries this week", remove the entry.

    It first appeared only for eight seconds after a log, reasoned as: a permanent undo on
    thirty-one rows is a standing invitation to delete something. The persona's counter is better,
    and it is the one this now follows — *this* is the screen they log on, so it is the screen that
    was taking recovery away, and a mis-tap noticed at the bottom of a staircase is noticed later
    than eight seconds. Today has always shown undo whenever `loggedOnDay > 0`; a row with nothing
    logged still shows no control, so it is never a button that does nothing.
  */
  // `recorded`, not `value > 0`: a tallied ceiling's `clean` logs 0 (`01a08096-e031`).
  const hasEntryToday =
    progress.byDay.find((d) => d.day === todayInZone(timezone))?.recorded ?? false;

  const undo = useMutation({
    /*
      No day sent at all — the route defaults `on` to the account's own day.

      This used to pass `todayIso()`, the browser's day, which for an account in another zone is a
      write aimed at the wrong date: with the browser behind the account it took back *yesterday's*
      entry and left today's standing, so the row still read as logged. `todayInZone(timezone)`
      would be correct too, and is deliberately not what this does — the server already knows the
      answer, and a day the client computes is one more clock that has to keep agreeing with it.
    */
    mutationFn: () => undoHabitLog(habit.id),
    onSuccess: invalidate,
  });
  const pause = useMutation({
    /*
      Paused from now, or resumed by clearing the end of the window.

      `activeTo` is an inclusive last day, so setting it to today would leave the habit running
      for the rest of today — and a Pause button that does nothing until tomorrow is a Pause
      button that looks broken. Yesterday is the honest encoding of "ran until yesterday".

      Yesterday *on the account's calendar*. This subtracted 86_400_000ms from `Date.now()` and
      took the UTC day of the result, so for an account at UTC+14 pressing Pause before 10:00 UTC
      the stored day landed two days back: yesterday's period became inapplicable, dropped out of
      the rate and both runs, and its bar stopped being drawn. The arithmetic was always right;
      the clock under it was not.
    */
    mutationFn: () =>
      updateHabit(habit.id, {
        activeTo: habit.activeTo ? null : shiftDay(todayInZone(timezone), -1),
      }),
    onSuccess: invalidate,
  });

  const archive = useMutation({
    mutationFn: () => updateHabit(habit.id, { archived: true }),
    onSuccess: invalidate,
  });
  const remove = useMutation({ mutationFn: () => deleteHabit(habit.id), onSuccess: invalidate });

  // Said by the service — see `HabitProgress.isReading`.
  const measured = progress.isReading;
  const run = runFigure(progress, habit.period);

  /*
    Stood down, and the card says that instead of judging around it.

    Pressing Pause used to change one chip: the sentence underneath went on reading "0 sessions of
    5 this week · too early to tell · 5 to go", the panel offered "Expected by now 0.7", and the
    quick-log was live — so a target the user had explicitly stood down kept accruing a debt. The
    state comes off the payload rather than from `habit.activeTo` alone, because the question is
    "is this in force today", and the server is the one holding today.
  */
  const paused = progress.state === 'paused';
  /*
    One clause where the verdict word was. Paused and unmeasured are different facts — one is
    "nobody was here", the other "I stood this down" — so this replaces the word rather than
    joining it, and it carries the date because "paused" alone leaves the reader asking since when.
  */
  const today = todayInZone(timezone);
  const verdictClause =
    paused && habit.activeTo
      ? habitPausedSince(habit.activeTo)
      : verdictWord(progress, { period: habit.period, asOf: today });
  /*
    A reading older than its own period has no distance to the target worth stating: *1.52 % to go*
    beside a body fat last read two months ago is measured from a number that no longer describes
    anyone (`01a15a31-5ea4`). The verdict clause says *not read today* instead.
  */
  const stale = staleReadingSince(progress, habit.period, today) !== null;
  /*
    The figure, from the one formatter every habit surface calls — see `habitFigure`. For a reading
    it is the last reading and, unless it was taken today, its day: which is also how long it has
    gone unread, so the *not read for 8 days* clause that used to follow the verdict is gone rather
    than saying the date a second time (`01a03813-b97d`).
  */
  const figure = habitFigure(
    { ...progress, unit: habit.unit, polarity: habit.polarity },
    { window: habit.period, asOf: today, today },
  );

  return (
    /* `color` has been in the schema, the API body, the serializer and the export since Phase 3
       and no component read it — so setting it changed nothing anywhere. Used as the card's
       accent stripe, which is what a per-habit colour is for when you have twenty of them. */
    <li
      ref={card}
      className={`card habit-card ${held ? 'is-held' : ''}`}
      data-testid="habit-card"
      data-habit={habit.name}
      style={habit.color ? { borderInlineStartColor: habit.color, borderInlineStartWidth: 3 } : {}}
    >
      <div className="habit-head">
        <ProgressRing progress={progress} polarity={habit.polarity} />
        <div className="habit-title">
          {/*
            The badge, without the date in a tooltip.

            "Paused since 2026-09-06" lived in a `title` attribute, which is a hover on a desktop
            and nothing at all on the phone this screen is mostly read on. The date is in the
            status sentence below now, as text, so this is a badge on the name and not the only
            place a fact is kept.
          */}
          {habit.activeTo && <span className="pill">paused</span>}
          <button className="link habit-name" onClick={() => setExpanded((v) => !v)}>
            {habit.icon ? `${habit.icon} ` : ''}
            {habit.name}
          </button>
          <p className="muted">
            {progress.isAbstinence ? (
              /*
                Today is in this number, and the run three words later is not.

                "20 clean days · clean · 19 days in a row · best 19" — one clean run, counted twice
                and differing by one, with nothing on the card to say why. Both are right:
                `cleanDayCount` runs back from today, while a run only counts a limit once the day
                it was kept is over, because a day with nothing logged yet is still a day that can
                be broken. The panel behind the card says 19 too, so the bare 20 was the one number
                on the screen without a stated predicate.

                Said rather than removed. Counting today is the encouraging half of an abstinence
                habit; what it owed the reader was the word. The ring used to show the same figure
                without it, and says none now (`ringFigure`), so this is the one place it is said.

                Only when today is actually in the count, and `count > 0` is not that test. Both
                numbers now skip a day the account was not here for (Phase 84), so a morning
                before anything has been logged reads "19 clean days · too early to tell": a
                positive count of finished days, with today not among them. Keyed on the flag the
                same walk sets, so the sentence and the number cannot part company — and it still
                covers the case it was written for, where at zero today is the breach.
              */
              <>
                {progress.cleanDayCount ?? 0} clean day
                {(progress.cleanDayCount ?? 0) === 1 ? '' : 's'}
                {progress.cleanDayCountIncludesToday && ' including today'} · {verdictClause}
              </>
            ) : measured ? (
              /*
                A reading, not a score out of a target. "78.84 kg of 78 this week" describes a
                body weight as if it were a workload half-finished; "78.84 kg · 0.84 kg to go"
                says the same numbers in the only order that means anything.

                The figure is the service's `lastReading`, and `band.toTarget` is measured from
                that same reading — so the two numbers in this sentence are one reading. A reading
                older than its own period has no distance either (`stale`), and the window's band
                is not needed to say so: *13.48 % 3 months ago · not read today*, never a
                distance from a reading nobody has taken since.
                They were not one reading before:
                a period nobody had read printed *0 %* beside a distance from yesterday's 13.2
                (`01a154cc-c8ac`), and a weekly `mean` printed the week's mean beside a distance
                from its latest reading (`01a03813-b97d`).

                The verdict word is here because the ring is `aria-hidden`, so without it the
                only signal that a measurement is past its ceiling was the ring turning amber.
                Colour alone, on precisely the habits the marginal-breach work was written for — a
                screen reader heard "80.6 kg, 0.6 kg to go" and nothing at all about the limit.

                It is not redundant with the distance: "0.6 kg to go" says how far, "just over"
                says which side of the line you are on.
              */
              <>
                {figure} ·{' '}
                {/* The distance is the same debt "5 to go" is, said in kilograms — so it goes
                    while the habit is stood down, for the reason on `verdictClause`. */}
                {!paused && !stale && progress.band && (
                  <>{toTargetLabel(progress.band.toTarget, habit.unit)} · </>
                )}
                {verdictClause}
              </>
            ) : (
              <>
                {figure} · {verdictClause}
                {progress.remaining !== undefined &&
                  progress.remaining > 0 &&
                  habit.polarity === 'positive' && (
                    /* `amount()`, like every other number on a habit row. `remaining` is a
                     subtraction, so a target of 12.5 against 5.04 arrives as
                     7.460000000000001 — the review pills were routed through this in an earlier
                     pass and this path was missed. */
                    <> · {amount(progress.remaining, null)} to go</>
                  )}
              </>
            )}
            {/*
              The streak, without expanding anything.

              It was only ever in `GET /api/habits/{id}/history`, which is only fetched when a
              card is opened — so eleven habits meant eleven clicks and eleven requests to find
              out whether any run was alive. It is now on the status row every card already has.
            */}
            {/*
              The run, and the best run beside it.

              It used to appear only while a streak was alive, so a dead run and a live one read
              the same — a row saying nothing about its streak might be on 0 or might simply be a
              measurement. And the best was two screens down inside the card, which is where every
              encouraging number on this screen used to live.
            */}
            {/* `0 days in a row`, not `no run`: the run's empty state is a number, said in the
                same words as Today's row and the tile behind this card (`runFigure`). */}
            {run && (
              <>
                {' · '}
                {/* One phrase: "best 4" qualifies the run beside it, and broke onto a line of its
                    own as a dangling fragment when the status sentence was long. */}
                <span className="habit-runs">
                  <span data-testid="habit-run">{run.run}</span>
                  {run.best && (
                    <span className="subtext" data-testid="habit-best">
                      {' · '}
                      {run.best}
                    </span>
                  )}
                </span>
              </>
            )}
          </p>
          {/*
            The run of days, on the row. This is the entire reward loop for someone logging several
            times a day, and it was ~3,300px down the page inside the expanded card — while the
            Today rail, a narrower surface, had been drawing it all along.
          */}
          <PeriodStrip
            strip={progress.strip}
            name={habit.name}
            period={habit.period}
            polarity={habit.polarity}
            today={todayInZone(timezone)}
            target={progress.target}
            unit={habit.unit}
            reading={progress.isReading}
          />
        </div>
        <div className="habit-actions row gap">
          {/*
            While it is paused, the way back — not the quick-log, and not a greyed-out one either.

            Hidden rather than disabled, and replaced rather than merely hidden. A disabled `+1`
            is the defect this screen was already reported for: an affordance drawn and withheld,
            with nothing saying why, which is the same complaint as the strip cell that looks like
            a tickbox and does nothing. And an empty gap where the button was leaves the reader
            with no answer to "so how do I start this again" — the only Resume was inside the
            expanded panel, three sections down, next to Delete.

            Logging itself is still allowed by the API, deliberately: backfilling a day you did do
            it is not the same act as being nudged to do it today.
          */}
          {paused ? (
            <>
              <button
                type="button"
                disabled={pause.isPending}
                data-testid="resume-habit"
                onClick={() => pause.mutate()}
              >
                Resume
              </button>
              {hasEntryToday && (
                <button
                  type="button"
                  className="link"
                  disabled={undo.isPending}
                  aria-label={`Undo the last ${habit.name} entry`}
                  onClick={() => undo.mutate()}
                >
                  undo
                </button>
              )}
            </>
          ) : (
            <LogHabit
              habit={{
                id: habit.id,
                name: habit.name,
                unit: habit.unit,
                polarity: habit.polarity,
                typicalValue: progress.typicalValue,
                aggregation: habit.aggregation,
                isReading: progress.isReading,
                isAbstinence: progress.isAbstinence,
              }}
              pending={log.isPending}
              onLog={(value) => log.mutate(value)}
              onUndo={hasEntryToday ? () => undo.mutate() : undefined}
              undoPending={undo.isPending}
            />
          )}
        </div>
      </div>

      {expanded && (
        <div className="panel">
          {habit.description && <p>{habit.description}</p>}
          {/*
            `preferredWeekdays` has been stored since Phase 3 and read by nothing. Shown here,
            worded as the hint it is.

            The Phase 33 note that used to sit here justified this by saying "the template
            equivalent is both honoured and displayed". That was wrong: the *template*
            `preferredWeekdays` is displayed and nothing more — it appears in no scheduling code
            either. `preferredWindows` is the planning hint that is actually honoured, and only in
            one path. Phase 43 settles both.
          */}
          {habit.preferredWeekdays.length > 0 && (
            <p className="muted">
              Usually {habit.preferredWeekdays.map((d) => WEEKDAY_NAMES[d]).join(', ')} — a planning
              hint, never a rule.
            </p>
          )}
          <p className="muted">
            {habit.polarity === 'positive' ? 'At least' : 'At most'}{' '}
            {amount(habit.targetCount, habit.unit)} per {habit.period} · period{' '}
            {readableRange(progress.bounds.start, progress.bounds.end)}
          </p>
          {/* The judgement first, then the raw record. A dot grid says when; the bars say
              whether. */}
          <HabitHistoryPanel progress={progress} timezone={timezone} />

          <h4>Every day, past year</h4>
          <Heatmap
            habitId={habit.id}
            /*
              The real target, including zero.

              `|| 1` turned abstinence's 0 into 1, so the component could not tell "at most 0" from
              "at most 1" — and every rule that distinguishes abstinence from a numeric ceiling was
              silently disabled. The guard it was standing in for is already inside: `overshoot`
              divides by `Math.max(max, 1)`, and nothing else here divides by it at all.
            */
            max={habit.targetCount}
            daysPerPeriod={progress.bounds.length}
            avoid={habit.polarity === 'negative'}
            reading={progress.isReading}
            period={habit.period}
            timezone={timezone}
            unit={habit.unit}
          />

          {editing ? (
            <EditHabitForm habit={habit} onDone={() => setEditing(false)} />
          ) : (
            <div className="detail-foot">
              <button onClick={() => setEditing(true)}>Edit</button>
            </div>
          )}

          <div className="detail-foot">
            {/*
              Pause, which is the answer people actually need and had no control for.

              `activeFrom`/`activeTo` have been columns since Phase 3 with exactly one reader.
              Without a pause the only exits were Archive — which reads as giving up — and Delete,
              which destroys the history you were proud of. Pausing keeps everything and stops the
              judging: the paused weeks are excluded from the rate, from both runs, and from the
              medians a target suggestion is drawn from.
            */}
            <button onClick={() => pause.mutate()} data-testid="pause-habit">
              {habit.activeTo ? 'Resume' : 'Pause'}
            </button>
            <button onClick={() => archive.mutate()}>Archive</button>
            <button
              className="danger"
              onClick={() => {
                if (confirm(`Delete "${habit.name}" and all its history?`)) remove.mutate();
              }}
            >
              Delete
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

function NewHabitForm({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState('');
  const [polarity, setPolarity] = useState<HabitPolarity>('positive');
  const [targetCount, setTargetCount] = useState('3');
  const [period, setPeriod] = useState<HabitPeriod>('week');
  const [unit, setUnit] = useState('');
  const [aggregation, setAggregation] = useState<HabitAggregation>('sum');
  const [goalId, setGoalId] = useState('');

  const create = useMutation({
    mutationFn: () =>
      createHabit({
        name: name.trim(),
        polarity,
        targetCount: Number(targetCount),
        period,
        ...(unit.trim() ? { unit: unit.trim() } : {}),
        aggregation,
        ...(goalId ? { goalId } : {}),
      }),
    onSuccess: onDone,
  });

  const isAbstinence = Number(targetCount) === 0;

  return (
    <form
      className="card"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim()) create.mutate();
      }}
    >
      <h2>New habit</h2>
      <p className="muted">
        Frequency targets, not streaks — “3× a week” survives a missed Tuesday. A target of zero
        means abstinence, counted in clean days.
      </p>

      <div className="fields">
        <label>
          Name
          <input value={name} placeholder="Gym" onChange={(e) => setName(e.target.value)} />
        </label>
        <label>
          Direction
          <select value={polarity} onChange={(e) => setPolarity(e.target.value as HabitPolarity)}>
            <option value="positive">At least this often</option>
            <option value="negative">At most this often</option>
          </select>
        </label>
        <label>
          Target
          {/* `step="any"`, as the edit form has always had. Without it the browser refuses 7.5,
              so a sleep target of 7.5 hours could be edited into existence but never created. */}
          <input
            type="number"
            min={0}
            step="any"
            value={targetCount}
            onChange={(e) => setTargetCount(e.target.value)}
          />
        </label>
        <label>
          Per
          <select value={period} onChange={(e) => setPeriod(e.target.value as HabitPeriod)}>
            <option value="day">day</option>
            <option value="week">week</option>
            <option value="month">month</option>
          </select>
        </label>
        <label>
          Unit (optional)
          <input value={unit} placeholder="sessions" onChange={(e) => setUnit(e.target.value)} />
        </label>
        <label>
          Measures
          {/*
            How a period's figure is read from the entries. Summing is right for a quantity you
            accumulate towards a target and meaningless for a measurement you take — a week of
            body-weight entries summed to 545 kg on a persona's account.
          */}
          <select
            value={aggregation}
            aria-label="How this is measured"
            onChange={(e) => setAggregation(e.target.value as HabitAggregation)}
          >
            <option value="sum">a total to reach (sessions, km, pages)</option>
            <option value="latest">the latest reading (weight, resting heart rate)</option>
            <option value="mean">the period average (body fat, HRV)</option>
          </select>
        </label>
        <GoalSelect value={goalId} onChange={setGoalId} />
      </div>

      {isAbstinence && (
        <p className="banner ok-banner">
          Target zero: this will be tracked as clean days, and any entry breaks the run.
        </p>
      )}
      {create.error instanceof ApiError && <p className="banner">{create.error.message}</p>}

      <div className="detail-foot">
        <button type="submit" className="primary" disabled={!name.trim() || create.isPending}>
          Create
        </button>
        <button type="button" className="secondary" onClick={onDone}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/**
 * How many habits the banner names before it hands off to the "Behind" filter.
 *
 * Six fits two rows at 375 without the warning becoming the screen.
 */
const ATTENTION_CHIPS = 6;

export function Habits({
  openHabitId,
  timezone,
}: {
  openHabitId?: string | null;
  /** The account's zone — see `timezone` on `HabitCard`, which is the only thing that reads it. */
  timezone: string;
}) {
  const [query, setQuery] = useState('');
  const [show, setShow] = useState<'all' | 'due' | 'behind'>('all');
  /*
    Undone first, remembered.

    Thirty-one cards in creation order make an 8,326px page, against 4,310px on the Today rail for
    the same job — so the screen named after habits was the *worse* place to log one, and the
    reviewer said so plainly. Ordering by what is still undone puts the work at the top without
    hiding anything, which is what the Behind filter does at the cost of hiding everything else.

    Remembered per browser like the tasks screen's scope, sort and view: a list order you have to
    re-choose is one you stop choosing.
  */
  const [order, setOrderState] = useState<HabitOrder>(() => readOrder('created'));
  // Written when chosen, not on every render — see `ORDER_KEY` in `habitOrder.ts`.
  const setOrder = (next: HabitOrder) => {
    writeOrder(next);
    setOrderState(next);
  };
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  // The management screen shows paused habits — one that vanished could never be resumed.
  const status = useQuery({
    queryKey: ['habits', 'status', 'with-paused'],
    queryFn: () => getHabitsStatus({ includePaused: true }),
  });

  const drifting = (status.data ?? []).filter((h) => h.isDrifting);

  /*
    "Due" is the habit whose period has room left in it — the ones a log would actually change.
    "Behind" is the server's own `isDrifting`, so the filter and the banner above agree by
    construction rather than by two similar-looking conditions kept in step by hand.
  */
  // Rows you have just logged stay where they are — the hold, and its history, in `habitOrder.ts`.
  // Touching `show`, `query` or `order` releases it: the three controls that mean "re-rank this now".
  const { held, arrange, hold } = useLogHold(JSON.stringify([show, query, order]));

  const matches = (p: HabitProgress) => {
    if (query.trim() !== '' && !p.habit.name.toLowerCase().includes(query.trim().toLowerCase()))
      return false;
    if (show === 'behind') return p.isDrifting;
    // Nothing a stood-down habit could be due for — see `undoneRank` for the same `onTrack` trap.
    if (show === 'due') return p.state !== 'paused' && (!p.onTrack || (p.remaining ?? 0) > 0);
    return true;
  };
  const filtered = (status.data ?? []).filter((p) => matches(p) || held.has(p.habit.id));
  const ranked = order === 'created' ? filtered : byUndone(filtered);
  const visible = arrange(ranked, (p) => p.habit.id);

  // The arrangement is captured before the log lands, from the list as drawn.
  const handleLogged = (id: string) =>
    hold(
      id,
      visible.map((p) => p.habit.id),
    );

  return (
    <section>
      <div className="capture">
        {/*
          The disclosure disappears while the form is open rather than relabelling itself `Cancel`.

          It used to become a second Cancel, so one form offered two ways out — a floating one above
          the card and the form's own, beside the commit. The reviewer photographed it on 1.39.0 and
          the same pair had already been filed on 1.10.0 (`01a0809a-3781`). Two dismiss controls and
          one commit control is the wrong count in the wrong direction, and the floating one is the
          one a reader has no reason to look for.

          Hidden, not disabled: a disabled control still occupies the eye and still has to explain
          itself. The form's own Cancel is the way out, and it sits where the decision is being made.
        */}
        {!adding && <button onClick={() => setAdding(true)}>New habit</button>}
      </div>

      {adding && (
        <NewHabitForm
          onDone={() => {
            setAdding(false);
            /*
              Everything, not just `['habits']`. A habit is no longer a leaf: it can serve a goal,
              which means creating one changes the goal tree — and with a ten second `staleTime`,
              a narrow invalidation left the goals screen showing a habit-less goal for ten
              seconds after the habit was made. Correct before this phase, wrong after it.
            */
            void queryClient.invalidateQueries();
          }}
        />
      )}

      {/*
        The names are buttons now. This banner named up to seven habits as inert text, on a screen
        with no way to find one habit among thirty-one — so it stated a problem and offered no way
        to reach it. Pressing one filters the list down to it.
      */}
      {drifting.length > 0 && (
        <div className="banner attention" data-testid="habit-attention">
          <p className="attention-label">Needs attention</p>
          {/*
            A list of chips, not a sentence with buttons in it.

            Making the names pressable was right and the shape was wrong: nineteen of them,
            comma-separated inside a paragraph, came out 29px tall on 21px baselines — so
            vertically adjacent targets *overlapped by 8px*, and one of them ("Fibre") was 33px
            wide. The health nut is one-handed on a phone, and a mis-tap here silently filters to
            the wrong habit. Inline text also announced nothing about being pressable; it read as
            prose and happened to be controls.

            Chips fix both halves at once: the box is the affordance, so pressability does not rest
            on colour alone (SC 1.4.1), and each one holds the app's 44px floor with real spacing
            between them.
          */}
          <ul className="attention-chips">
            {drifting.slice(0, ATTENTION_CHIPS).map((h) => (
              <li key={h.habit.id}>
                <button
                  className="attention-chip"
                  /*
                    Named for what it does, not for what it says. The card below carries a button
                    with the same visible text — the habit's own name — so without this there are
                    two identically-named controls doing different things, and a screen reader
                    hearing "Gym, button" in a banner has no way to tell which one it landed on.
                  */
                  aria-label={`Show only ${h.habit.name}`}
                  onClick={() => setQuery(h.habit.name)}
                >
                  {h.habit.name}
                </button>
              </li>
            ))}
            {drifting.length > ATTENTION_CHIPS && (
              <li>
                {/*
                  The overflow goes to the filter that already exists rather than growing the
                  banner. Chips are four times the height of the words they replaced, so at the
                  health nut's thirty-one habits an uncapped banner is 460px — the entire first
                  screen of a phone, spent on a warning, with the habits it is warning about pushed
                  below the fold. "Behind" is the same set, in the list, where they can be logged.
                */}
                <button
                  className="attention-chip is-more"
                  onClick={() => {
                    setQuery('');
                    setShow('behind');
                  }}
                >
                  +{drifting.length - ATTENTION_CHIPS} more
                </button>
              </li>
            )}
          </ul>
        </div>
      )}

      <QueryState query={status} />
      {status.data?.length === 0 && !adding && (
        <p className="muted">No habits yet. Start with something you already half-do.</p>
      )}

      {/*
        A way to find one habit among thirty-one.

        Thirty-one rows in one flat list, logged several times a day, with no search, no filter and
        no order but the one they were created in. "Behind" is the view that matters most often —
        it is what the banner above is about — so it is a filter rather than only a colour.
      */}
      {/*
        Shown whenever there *is* a filter, not only when the list is long.

        The banner's habit names set the search query, and the field that shows the query only
        rendered above four habits — so an account with exactly four lost three cards to a filter
        with no visible control, no count, no chip and no URL change, which means the back button
        did not undo it either. A safety banner that removes data from view with no way back is
        worse than one that says nothing.

        Below five habits the controls are still noise you have to look past, which is why the
        threshold exists; a filter that is actually on is not noise.
      */}
      {((status.data?.length ?? 0) > 4 || query !== '' || show !== 'all') && (
        <div className="habit-filter">
          <input
            type="search"
            value={query}
            placeholder={`Filter ${status.data?.length ?? 0} habits`}
            aria-label="Filter habits"
            onChange={(e) => setQuery(e.target.value)}
          />
          {/*
            Order, beside the filter, because they answer neighbouring questions.

            A filter hides; an order does not. On a thirty-one row list the reader mostly wants the
            undone ones nearest the thumb without losing sight of the rest, which is a sort rather
            than a filter — and the filter is what they were using for it, at the cost of the rest
            of the list.
          */}
          <button
            type="button"
            className="link"
            data-testid="habit-order"
            aria-pressed={order === 'undone'}
            onClick={() => setOrder(order === 'undone' ? 'created' : 'undone')}
          >
            {order === 'undone' ? 'In the order you made them' : 'Undone first'}
          </button>
          {/* The way back, beside the thing that took it away. */}
          {query !== '' && (
            <button className="link" onClick={() => setQuery('')} data-testid="clear-habit-filter">
              Showing “{query}” — show all
            </button>
          )}
          <div className="view-toggle" role="group" aria-label="Which habits">
            {(['all', 'due', 'behind'] as const).map((f) => (
              <button
                key={f}
                type="button"
                className={show === f ? 'is-active' : ''}
                aria-pressed={show === f}
                onClick={() => setShow(f)}
              >
                {f === 'all' ? 'All' : f === 'due' ? 'Due now' : 'Behind'}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* The key to every strip in the list below, once, where the filter row ends. */}
      {visible.some((p) => p.strip.length > 0) && <StripLegend />}
      <ul className="habit-list">
        {visible.map((progress) => (
          <HabitCard
            key={progress.habit.id}
            progress={progress}
            timezone={timezone}
            held={!matches(progress)}
            reveal={progress.habit.id === openHabitId}
            onLogged={handleLogged}
          />
        ))}
      </ul>
      {visible.length === 0 && (status.data?.length ?? 0) > 0 && (
        <p className="muted">No habit matches that.</p>
      )}
    </section>
  );
}
